import { createFileRoute } from "@tanstack/react-router";
import { LegalPage } from "@/components/legal-page";
import { socialImageMeta, breadcrumbLd } from "@/lib/seo";

export const Route = createFileRoute("/legal/seller-agreement")({
  head: () => ({
    meta: [
      { title: "Seller Agreement — BidNova Auctions" },
      { name: "description", content: "The Seller Agreement for listing graded Pokémon cards on BidNova Auctions, a UK auction platform governed by English law." },
      { property: "og:title", content: "Seller Agreement — BidNova Auctions" },
      { property: "og:description", content: "Obligations, fees, payouts and prohibited items for sellers." },
      { property: "og:url", content: "https://bidnovaauctions.uk/legal/seller-agreement" },
      { property: "og:type", content: "article" },
      ...socialImageMeta(),
      { name: "twitter:card", content: "summary" },
    ],
    links: [{ rel: "canonical", href: "https://bidnovaauctions.uk/legal/seller-agreement" }],
    scripts: [breadcrumbLd([{ name: "Home", path: "/" }, { name: "Legal", path: "/legal/terms" }, { name: "Seller Agreement", path: "/legal/seller-agreement" }])],
  }),
  component: SellerAgreementPage,
});

function SellerAgreementPage() {
  return (
    <LegalPage eyebrow="Legal" title="Seller Agreement" updated="7 July 2026">
      <p>
        This Seller Agreement applies to anyone listing an item for sale on
        bidnovaauctions.uk (the "Platform"), operated by BidNova Auctions
        Auctions Ltd, a company registered in England &amp; Wales. It
        supplements our <a href="/legal/terms">Terms &amp; Conditions</a> and
        <a href="/legal/privacy"> Privacy Policy</a>. In case of conflict, this
        Agreement prevails for seller-specific matters.
      </p>

      <h2>1. Eligibility</h2>
      <ul>
        <li>You must be at least 18 and resident in the UK, or a UK-registered business.</li>
        <li>You must have full legal title to every item you list and the right to sell it.</li>
        <li>Businesses selling in the course of trade must comply with the Consumer Rights Act 2015 and the Consumer Contracts (Information, Cancellation and Additional Charges) Regulations 2013.</li>
      </ul>

      <h2>2. Listing accuracy</h2>
      <ul>
        <li>Descriptions, photographs, grades, and condition assessments must be accurate and complete.</li>
        <li>All PSA, BGS or CGC certification numbers you provide must match the slab and be verifiable via the grading company's public database.</li>
        <li>You must disclose all known defects — creasing, whitening, edge wear, scratches on the slab, reholder history, etc.</li>
        <li>Photographs must be of the actual item on offer, not stock images.</li>
        <li>Misrepresentation is a breach of this Agreement and may constitute an offence under the Fraud Act 2006.</li>
      </ul>

      <h2>3. Auction rules for sellers</h2>
      <ul>
        <li>You may not bid on your own auctions, or arrange for others to do so ("shill bidding" is prohibited and is a breach of the Consumer Protection from Unfair Trading Regulations 2008).</li>
        <li>You may set a reserve; if bidding does not reach the reserve, no sale is concluded.</li>
        <li>You may not cancel an auction that has received bids except in exceptional circumstances agreed with us.</li>
        <li>Once the auction ends and the reserve (if any) is met, you are contractually bound to sell to the winning bidder.</li>
      </ul>

      <h2>4. Payment flow and payouts</h2>
      <ul>
        <li>Buyers pay via Stripe on our Platform. We hold funds on your behalf until the transaction is complete.</li>
        <li>Our commission is a flat <strong>8%</strong> of the winning bid, deducted before payout. There are no listing fees, no monthly fees and no subscription fees — commission is only charged on successful sales.</li>
        <li>Stripe processing fees may also apply and are set out on your Sales page.</li>
        <li>Payouts are released manually by an admin once: (a) the buyer has confirmed delivery, or 72 hours have elapsed since delivery without a dispute; and (b) no dispute is active on the order.</li>
        <li>Payouts are made in GBP by bank transfer to the UK bank account on your account profile.</li>
      </ul>

      <h2>5. Shipping obligations</h2>
      <ul>
        <li>Dispatch within 5 business days of payment confirmation.</li>
        <li>Use a tracked and signed-for service (e.g. Royal Mail Special Delivery for UK orders).</li>
        <li>Upload the tracking number on the order page — the buyer is notified automatically.</li>
        <li>Package cards so that they are protected against bending, moisture and impact.</li>
        <li>You bear the risk of loss or damage in transit until the buyer confirms delivery.</li>
      </ul>

      <h2>6. Buyer protection and disputes</h2>
      <ul>
        <li>Buyers may open a dispute if the item is not as described, arrives damaged, or fails to arrive.</li>
        <li>No payout will be released while a dispute is active.</li>
        <li>You must respond to disputes promptly and provide any evidence requested by our team.</li>
        <li>Where an admin resolves a dispute in the buyer's favour, we may refund the buyer and recover funds from you (including via reversal of a future payout).</li>
      </ul>

      <h2>7. Prohibited items</h2>
      <p>You must not list:</p>
      <ul>
        <li>Counterfeit or reproduction cards, altered slabs, or items with tampered grading labels.</li>
        <li>Items you do not own or have the right to sell.</li>
        <li>Items infringing the intellectual property rights of The Pokémon Company, Nintendo, or any third party.</li>
        <li>Items subject to UK export controls or trade sanctions.</li>
        <li>Any item unlawful to sell under English law.</li>
      </ul>
      <p>Listings that breach these rules will be removed and may result in suspension of your account.</p>

      <h2>8. Tax</h2>
      <ul>
        <li>You are solely responsible for declaring income from sales to HMRC and, where applicable, for VAT.</li>
        <li>Under the OECD Model Reporting Rules for Digital Platforms, we may be required to share transaction data with HMRC.</li>
      </ul>

      <h2>9. Suspension and termination</h2>
      <p>
        We may suspend or terminate your seller privileges for breach of this
        Agreement, fraudulent conduct, repeated disputes decided against you,
        or where required by law. Funds already earned on completed, undisputed
        orders remain payable to you subject to any set-off for refunds owed.
      </p>

      <h2>10. Governing law and jurisdiction</h2>
      <p>
        This Agreement is governed by the laws of England &amp; Wales. The
        courts of England &amp; Wales have exclusive jurisdiction over any
        dispute arising out of or in connection with it.
      </p>

      <h2>11. Contact</h2>
      <p>
        Sellers: <a href="mailto:sellers@bidnovaauctions.uk">sellers@bidnovaauctions.uk</a>.
      </p>
    </LegalPage>
  );
}
